import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import ExamForm from '../components/ExamForm';
import { ChevronDown, ChevronRight, FlaskConical, RefreshCw, ExternalLink, Pencil, Trash2, ClipboardCheck } from 'lucide-react';
import { hasPermission } from '../lib/permissions';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';
import { buildAppDateTimeIso, toAppDateInputValue, toAppTimeInputValue } from '../lib/appDateTime';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'solicitado', label: 'Solicitado' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'concluido', label: 'Concluído' },
  { value: 'atrasado', label: 'Atrasado' },
  { value: 'cancelado', label: 'Cancelado' }
];

function NecropsySampleMetadata({ request }) {
  const data = request.sample_metadata;
  if (!data || !(request.source_type === 'necropsy' || request.necropsy_id)) return null;
  const rows = [
    ['Nome do exame', data.exam_type_other],
    ['Órgão/Amostra', [...(data.organ_samples || []), ...(data.organ_samples_other || [])].join(', ')],
    ['Local da coleta', data.sample_collection_site],
    ['Local do Swab', data.swab_collection_site],
    ['Método de preservação', [data.preservation_method, data.preservation_method_other].filter(Boolean).join(': ')],
    ['Armazenado em', [data.storage_container, data.storage_container_other].filter(Boolean).join(': ')],
    ['Laboratório destino', [data.destination_lab, data.destination_lab_other].filter(Boolean).join(': ')]
  ].filter(([, value]) => value);
  if (!rows.length) return null;
  return <div className="rounded border bg-emerald-50 p-3 text-sm"><p className="font-semibold">Coleta informada na Necropsia</p>{rows.map(([label, value]) => <p key={label}><strong>{label}:</strong> {value}</p>)}</div>;
}

export default function LabRequestsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [requests, setRequests] = useState([]);
  const [generatedExams, setGeneratedExams] = useState([]);
  const [summary, setSummary] = useState(null);
  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [filterBase, setFilterBase] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLaboratoryType, setFilterLaboratoryType] = useState('all');
  const [filterCity, setFilterCity] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [animalFilter, setAnimalFilter] = useState(searchParams.get('animal_id') || '');
  const [debouncedAnimalFilter, setDebouncedAnimalFilter] = useState(animalFilter);
  const [generatedPage, setGeneratedPage] = useState(1);
  const [generatedPageSize, setGeneratedPageSize] = useState(25);
  const [generatedPagination, setGeneratedPagination] = useState({
    total: 0,
    page: 1,
    page_size: 25,
    total_pages: 0
  });
  const [loadError, setLoadError] = useState('');
  const [notesById, setNotesById] = useState({});
  const [centerMode, setCenterMode] = useState('requests');
  const [viewMode, setViewMode] = useState('list');
  const [expandedGeneratedAnimals, setExpandedGeneratedAnimals] = useState({});
  const [draggingRequestId, setDraggingRequestId] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);
  const [registeringRequest, setRegisteringRequest] = useState(null);
  const [registeringExamType, setRegisteringExamType] = useState('');
  const [linkingRequest, setLinkingRequest] = useState(null);
  const [linkableExams, setLinkableExams] = useState([]);
  const [loadingLinkableExams, setLoadingLinkableExams] = useState(false);
  const [linkingExamId, setLinkingExamId] = useState(null);
  const [editForm, setEditForm] = useState({
    exam_type: '',
    laboratory_type: 'interno',
    requested_date: '',
    requested_time: '',
    requester_name: '',
    notes: '',
    status: 'solicitado',
    result_text: ''
  });
  const canUpdateRequests = hasPermission(user, 'lab_requests.update');
  const canCreateExam = hasPermission(user, 'exams.create');
  const canManageRequests = ['administrador', 'programador'].includes(user?.role);
  const latestRequestRef = useRef(0);
  const requestedAnimalFilter = searchParams.get('animal_id') || '';

  useEffect(() => {
    setAnimalFilter(requestedAnimalFilter);
  }, [requestedAnimalFilter]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedAnimalFilter(animalFilter.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [animalFilter]);

  useEffect(() => {
    fetchBases();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchLabCenterData(controller.signal);
    return () => controller.abort();
  }, [centerMode, filterBase, filterStatus, filterLaboratoryType, filterCity, filterStartDate, filterEndDate, debouncedAnimalFilter, generatedPage, generatedPageSize]);

  useEffect(() => {
    setGeneratedPage(1);
  }, [centerMode, filterBase, filterStatus, filterLaboratoryType, filterCity, filterStartDate, filterEndDate, animalFilter, generatedPageSize]);

  const fetchBases = async () => {
    try {
      const response = await axios.get(`${API}/bases`);
      setBases(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const buildSharedParams = () => {
    const params = new URLSearchParams();
    if (filterBase !== 'all') params.append('base_id', filterBase);
    if (filterLaboratoryType !== 'all') params.append('laboratory_type', filterLaboratoryType);
    if (filterCity.trim()) params.append('origin_city', filterCity.trim());
    if (filterStartDate) params.append('start_date', filterStartDate);
    if (filterEndDate) params.append('end_date', filterEndDate);
    if (debouncedAnimalFilter) params.append('animal_id', debouncedAnimalFilter);
    return params;
  };

  const buildGeneratedSummary = (exams) => {
    const nextSummary = {
      total: exams.length,
      by_laboratory_type: { interno: 0, externo: 0 },
      by_status: { concluido: exams.length, atrasado: 0 },
      by_base: {}
    };
    exams.forEach((exam) => {
      const laboratoryType = exam.laboratory_type || (exam.categoria === 'Externo' ? 'externo' : 'interno');
      nextSummary.by_laboratory_type[laboratoryType] = (nextSummary.by_laboratory_type[laboratoryType] || 0) + 1;
      const baseName = exam.base_name || exam.base_id || 'N/A';
      nextSummary.by_base[baseName] = (nextSummary.by_base[baseName] || 0) + 1;
    });
    return nextSummary;
  };

  const updateFilter = (setter) => (value) => {
    setter(value);
    setGeneratedPage(1);
  };

  const fetchLabCenterData = async (signal) => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setLoading(true);
    setLoadError('');
    try {
      const params = buildSharedParams();
      if (centerMode === 'requests') {
        if (filterStatus !== 'all') params.append('status', filterStatus);
        const [requestsResponse, summaryResponse] = await Promise.all([
          axios.get(`${API}/lab-requests?${params.toString()}`, { signal }),
          axios.get(`${API}/lab-requests/summary?${params.toString()}`, { signal })
        ]);
        if (requestId !== latestRequestRef.current) return;
        setRequests(requestsResponse.data || []);
        setSummary(summaryResponse.data || null);
      } else {
        if (filterStatus !== 'all') params.append('status', filterStatus);
        params.append('page', String(generatedPage));
        params.append('page_size', String(generatedPageSize));
        const response = await axios.get(`${API}/lab-exams?${params.toString()}`, { signal });
        if (requestId !== latestRequestRef.current) return;
        const payload = response.data || {};
        const exams = Array.isArray(payload) ? payload : (payload.items || []);
        setGeneratedExams(exams);
        setSummary(payload.summary || buildGeneratedSummary(exams));
        setGeneratedPagination({
          total: payload.total ?? exams.length,
          page: payload.page ?? generatedPage,
          page_size: payload.page_size ?? generatedPageSize,
          total_pages: payload.total_pages ?? 1
        });
      }
    } catch (error) {
      if (axios.isCancel?.(error) || error.name === 'CanceledError') return;
      const message = centerMode === 'requests' ? 'Erro ao carregar solicitações laboratoriais' : 'Erro ao carregar exames gerados';
      setLoadError(message);
      toast.error(message);
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchRequests = () => fetchLabCenterData();

  const handleUpdateStatus = async (requestId, status) => {
    setSavingId(requestId);
    try {
      await axios.put(`${API}/lab-requests/${requestId}`, {
        status,
        note: notesById[requestId] || null,
        result_text: status === 'concluido' ? (notesById[requestId] || null) : undefined
      });
      toast.success('Solicitação atualizada');
      setNotesById((prev) => ({ ...prev, [requestId]: '' }));
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar solicitação');
    } finally {
      setSavingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'solicitado': return 'bg-amber-100 text-amber-800';
      case 'em_andamento': return 'bg-sky-100 text-sky-800';
      case 'concluido': return 'bg-emerald-100 text-emerald-800';
      case 'atrasado': return 'bg-orange-100 text-orange-800';
      case 'cancelado': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const kanbanColumns = STATUS_OPTIONS.filter((option) => option.value !== 'all');

  const handleDropOnStatus = async (status) => {
    if (!draggingRequestId || !canUpdateRequests) return;
    setDraggingRequestId(null);
    await handleUpdateStatus(draggingRequestId, status);
  };

  const formatDateInput = (value) => {
    return toAppDateInputValue(value || new Date());
  };

  const formatTimeInput = (value) => {
    return toAppTimeInputValue(value || new Date());
  };

  const buildDateTimeIso = (dateValue, timeValue) => {
    return buildAppDateTimeIso(dateValue, timeValue);
  };

  const openEditDialog = (request) => {
    setEditingRequest(request);
    setEditForm({
      exam_type: request.exam_type || request.requested_exam_types?.[0] || '',
      laboratory_type: request.laboratory_type || 'interno',
      requested_date: formatDateInput(request.requested_at || request.created_at),
      requested_time: formatTimeInput(request.requested_at || request.created_at),
      requester_name: request.requester_name || request.created_by_name || '',
      notes: request.notes || '',
      status: request.status || 'solicitado',
      result_text: request.result_text || ''
    });
  };

  const updateEditForm = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editingRequest) return;
    setSavingId(editingRequest.id);
    try {
      await axios.put(`${API}/lab-requests/${editingRequest.id}`, {
        exam_type: editForm.exam_type,
        laboratory_type: editForm.laboratory_type,
        requested_at: buildDateTimeIso(editForm.requested_date, editForm.requested_time),
        requester_name: editForm.requester_name,
        notes: editForm.notes,
        status: editForm.status,
        result_text: editForm.result_text || null
      });
      toast.success('Solicitação editada');
      setEditingRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao editar solicitação');
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteRequest = async (request) => {
    if (!window.confirm(`Excluir a solicitação ${request.request_number || request.exam_type || ''}?`)) return;
    setSavingId(request.id);
    try {
      await axios.delete(`${API}/lab-requests/${request.id}`);
      toast.success('Solicitação excluída');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir solicitação');
    } finally {
      setSavingId(null);
    }
  };

  const getRequestedExamTypes = (request) => {
    const types = [...(request.requested_exam_types || [])];
    if (request.exam_type && !types.includes(request.exam_type)) types.unshift(request.exam_type);
    return types.filter(Boolean);
  };

  const normalizeExamType = (value) => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '');

  const openLinkExamDialog = async (request) => {
    setLinkingRequest(request);
    setLinkableExams([]);
    setLoadingLinkableExams(true);
    try {
      const response = await axios.get(`${API}/animals/${request.animal_id}/exams`);
      const requestedTypes = new Set(getRequestedExamTypes(request).map(normalizeExamType));
      setLinkableExams((response.data || []).filter((exam) => (
        !exam.lab_request_id && !exam.cancelled_at && requestedTypes.has(normalizeExamType(exam.tipo_exame))
      )));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao buscar exames do animal');
    } finally {
      setLoadingLinkableExams(false);
    }
  };

  const handleLinkExam = async (examId) => {
    if (!linkingRequest) return;
    setLinkingExamId(examId);
    try {
      await axios.post(`${API}/lab-requests/${linkingRequest.id}/exams/link`, { exam_id: examId });
      toast.success('Exame vinculado à solicitação');
      setLinkingRequest(null);
      setLinkableExams([]);
      await fetchLabCenterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao vincular exame');
    } finally {
      setLinkingExamId(null);
    }
  };

  const openRegisterExamDialog = (request, examType = '') => {
    if (request.status === 'cancelado') {
      toast.error('Solicitação cancelada não permite registrar exame');
      return;
    }
    const requestedTypes = getRequestedExamTypes(request);
    setRegisteringRequest(request);
    setRegisteringExamType(examType || requestedTypes.find((type) => {
      const linked = (request.linked_exams || []).find((item) => item.exam_type === type);
      return linked?.status !== 'concluido';
    }) || requestedTypes[0] || request.exam_type || '');
  };

  const handleExamRegistered = async () => {
    setRegisteringRequest(null);
    setRegisteringExamType('');
    await fetchLabCenterData();
    if (centerMode !== 'generated') {
      setGeneratedPage(1);
    }
  };

  const buildExamInitialValuesFromRequest = () => {
    if (!registeringRequest) return {};
    return {
      lab_request_id: registeringRequest.id,
      requested_exam_type: registeringExamType,
      tipo_exame: registeringExamType || registeringRequest.exam_type || '',
      categoria: registeringRequest.laboratory_type === 'externo' ? 'Externo' : 'Interno',
      laboratorio_origem: registeringRequest.laboratory_name || registeringRequest.laboratorio_origem || ''
    };
  };

  const renderLinkedExams = (request) => {
    const linked = request.linked_exams || [];
    if (!linked.length) return null;
    return (
      <div className="rounded border bg-gray-50 p-3 text-sm">
        <p className="font-medium text-gray-700 mb-2">Exames vinculados</p>
        <div className="grid gap-2 md:grid-cols-2">
          {linked.map((item) => (
            <div key={`${request.id}-${item.exam_type}`} className="flex flex-wrap items-center justify-between gap-2 rounded bg-white p-2 border">
              <span>{item.exam_type} - {item.status.replace('_', ' ')}</span>
              {item.exam_id && (
                <Button size="sm" variant="outline" onClick={() => navigate(`/animals/${request.animal_id}/record?tab=exams&exam_id=${item.exam_id}`)}>
                  Ver exame
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleDownloadExamPdf = async (exam) => {
    try {
      const response = await axios.get(`${API}/exams/${exam.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `exame_${exam.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar PDF do exame');
    }
  };

  const generatedExamsByAnimal = generatedExams.reduce((groups, exam) => {
    const key = exam.animal_id || exam.id;
    if (!groups[key]) {
      groups[key] = {
        animal_id: exam.animal_id,
        ibama_id: exam.ibama_id,
        pmp_id: exam.pmp_id,
        common_name: exam.common_name,
        species: exam.species,
        base_name: exam.base_name,
        origin_city: exam.origin_city,
        exams: []
      };
    }
    groups[key].exams.push(exam);
    return groups;
  }, {});
  const generatedAnimalGroups = Object.values(generatedExamsByAnimal);

  const toggleGeneratedAnimal = (animalKey) => {
    setExpandedGeneratedAnimals((prev) => ({
      ...prev,
      [animalKey]: !prev[animalKey]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <header className="bg-white shadow-sm border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="hidden md:flex items-center justify-between gap-6">
            <div className="flex items-center gap-3 min-w-0">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img src="/bw-symbol-final.png" alt="Instituto BW" className="h-12 w-12 object-contain" />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}`}>
                <h1 className="text-xl font-bold text-emerald-800 truncate">Central do Laboratório</h1>
                <p className="text-xs text-emerald-500 truncate">Solicitações de exames a partir do acompanhamento</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchRequests}><RefreshCw className="w-4 h-4" /></Button>
              <Button variant="outline" onClick={() => navigate('/')}>Voltar</Button>
              <Button variant="ghost" className="text-red-600" onClick={logout}>Sair</Button>
            </div>
          </div>

          <div className="md:hidden space-y-4">
            <div className="flex items-start gap-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img src="/bw-symbol-final.png" alt="Instituto BW" className="h-11 w-11 object-contain" />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}`}>
                <h1 className="text-lg font-bold text-emerald-800 break-words">Central do Laboratório</h1>
                <p className="text-xs text-emerald-500 break-words">Solicitações de exames a partir do acompanhamento</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" onClick={fetchRequests} className="w-full"><RefreshCw className="w-4 h-4" /></Button>
              <Button variant="outline" onClick={() => navigate('/')} className="w-full">Voltar</Button>
              <Button variant="ghost" className="w-full text-red-600" onClick={logout}>Sair</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <Card>
          <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-7 gap-4">
            <div>
              <Label>Base</Label>
              <Select value={filterBase} onValueChange={updateFilter(setFilterBase)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as bases</SelectItem>
                  {bases.map((base) => <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={filterStatus} onValueChange={updateFilter(setFilterStatus)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Laboratório</Label>
              <Select value={filterLaboratoryType} onValueChange={updateFilter(setFilterLaboratoryType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="interno">Interno</SelectItem>
                  <SelectItem value="externo">Externo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cidade de origem</Label>
              <Input value={filterCity} onChange={(e) => updateFilter(setFilterCity)(e.target.value)} placeholder="Ex: Santos" />
            </div>
            <div>
              <Label>Buscar animal / ID IBAMA</Label>
              <Input value={animalFilter} onChange={(e) => updateFilter(setAnimalFilter)(e.target.value)} placeholder="REPT 1160 ou 1160" />
            </div>
            <div>
              <Label>Início</Label>
              <Input type="date" value={filterStartDate} onChange={(e) => updateFilter(setFilterStartDate)(e.target.value)} />
            </div>
            <div>
              <Label>Fim</Label>
              <Input type="date" value={filterEndDate} onChange={(e) => updateFilter(setFilterEndDate)(e.target.value)} />
            </div>
          </CardContent>
        </Card>

        {summary && (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Card><CardContent className="p-4"><p className="text-xs text-gray-500">Total</p><p className="text-2xl font-bold text-emerald-800">{summary.total || 0}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-gray-500">Internos</p><p className="text-2xl font-bold text-sky-800">{summary.by_laboratory_type?.interno || 0}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-gray-500">Externos</p><p className="text-2xl font-bold text-violet-800">{summary.by_laboratory_type?.externo || 0}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-gray-500">Concluídos</p><p className="text-2xl font-bold text-emerald-800">{summary.by_status?.concluido || 0}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-gray-500">Atrasados</p><p className="text-2xl font-bold text-orange-800">{summary.by_status?.atrasado || 0}</p></CardContent></Card>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            variant={centerMode === 'requests' ? 'default' : 'outline'}
            className={centerMode === 'requests' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            onClick={() => setCenterMode('requests')}
          >
            Solicitações
          </Button>
          <Button
            variant={centerMode === 'generated' ? 'default' : 'outline'}
            className={centerMode === 'generated' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            onClick={() => {
              setCenterMode('generated');
              setViewMode('list');
            }}
          >
            Exames gerados
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            className={viewMode === 'list' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            onClick={() => setViewMode('list')}
          >
            Lista
          </Button>
          {centerMode === 'requests' && (
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'outline'}
              className={viewMode === 'kanban' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              onClick={() => setViewMode('kanban')}
            >
              Kanban por Status
            </Button>
          )}
        </div>

        {loading ? (
          <Card><CardContent className="py-12 text-center">{centerMode === 'generated' ? 'Carregando exames...' : 'Carregando solicitações...'}</CardContent></Card>
        ) : loadError ? (
          <Card><CardContent className="py-12 text-center text-red-600">{loadError}</CardContent></Card>
        ) : centerMode === 'generated' ? (
          generatedExams.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">Nenhum exame encontrado para os filtros selecionados.</CardContent></Card>
          ) : (
            <div className="space-y-4">
              <Card>
                <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-600">
                    Página {generatedPagination.page || 1} de {generatedPagination.total_pages || 1} • {generatedPagination.total || 0} registro(s)
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={String(generatedPageSize)} onValueChange={(value) => setGeneratedPageSize(Number(value))}>
                      <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[20, 25, 50, 100].map((size) => (
                          <SelectItem key={size} value={String(size)}>{size}/página</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      disabled={(generatedPagination.page || 1) <= 1}
                      onClick={() => setGeneratedPage((current) => Math.max(1, current - 1))}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      disabled={(generatedPagination.page || 1) >= (generatedPagination.total_pages || 1)}
                      onClick={() => setGeneratedPage((current) => current + 1)}
                    >
                      Próxima
                    </Button>
                  </div>
                </CardContent>
              </Card>
              {generatedAnimalGroups.map((group) => {
                const groupKey = group.animal_id || group.ibama_id;
                const isExpanded = Boolean(expandedGeneratedAnimals[groupKey]);
                return (
                <Card key={groupKey} className="border-l-4 border-l-sky-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex flex-wrap items-center justify-between gap-3 text-base">
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-sky-700"
                          onClick={() => toggleGeneratedAnimal(groupKey)}
                          aria-label={isExpanded ? 'Recolher exames do animal' : 'Expandir exames do animal'}
                        >
                          {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        </Button>
                        <FlaskConical className="w-4 h-4 text-sky-700" />
                        <span>{group.ibama_id || 'Sem IBAMA'} - {group.common_name || 'Animal'}</span>
                        {group.pmp_id && <Badge className="bg-sky-100 text-sky-700">PMP {group.pmp_id}</Badge>}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className="bg-emerald-100 text-emerald-800">{group.exams.length} exame(s)</Badge>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid gap-2 text-sm md:grid-cols-2">
                      <p><strong>Espécie:</strong> {group.species || 'N/A'}</p>
                      <p><strong>Base:</strong> {group.base_name || 'N/A'}</p>
                      <p><strong>Origem:</strong> {group.origin_city || 'N/A'}</p>
                    </div>
                    {isExpanded ? (
                    <div className="space-y-3">
                      {group.exams.map((exam) => (
                        <div key={exam.id} className="rounded-lg border bg-white p-3">
                          <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p className="font-semibold text-gray-900">{exam.tipo_exame || 'Exame'}</p>
                              <p className="text-xs text-gray-600">
                                {exam.data_coleta ? new Date(exam.data_coleta).toLocaleString('pt-BR') : 'Data não informada'} • {exam.responsavel || 'Responsável N/A'}
                              </p>
                            </div>
                            <Badge className={exam.categoria === 'Externo' ? 'bg-violet-100 text-violet-800' : 'bg-sky-100 text-sky-800'}>
                              {exam.categoria === 'Externo' ? (exam.laboratorio_origem || 'Externo') : 'Interno'}
                            </Badge>
                          </div>
                          {exam.observacoes && <p className="rounded border bg-emerald-50 p-2 text-sm"><strong>Resultado:</strong> {exam.observacoes}</p>}
                          {exam.parametros?.length > 0 && (
                            <div className="mt-2 rounded border bg-gray-50 p-2 text-sm">
                              <p className="mb-2 font-medium text-gray-700">Resultados estruturados</p>
                              <div className="grid gap-2 md:grid-cols-2">
                                {exam.parametros.slice(0, 4).map((param, index) => (
                                  <div key={`${exam.id}-param-${index}`} className="rounded bg-white p-2 border">
                                    <p><strong>{param.nome_parametro}</strong></p>
                                    <p>{param.valor || param.resultado || '-'} {param.unidade || ''}</p>
                                    {(param.intensidade || param.agente_identificado) && (
                                      <p className="text-xs text-gray-600">{[param.intensidade, param.agente_identificado].filter(Boolean).join(' • ')}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="mt-3 flex flex-wrap justify-end gap-2">
                            <Button variant="outline" onClick={() => handleDownloadExamPdf(exam)}>
                              PDF
                            </Button>
                            <Button variant="outline" onClick={() => navigate(`/animals/${exam.animal_id}/record?tab=exams&exam_id=${exam.id}`)}>
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Prontuário
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    ) : (
                      <button
                        type="button"
                        className="w-full rounded-lg border border-dashed border-sky-200 bg-sky-50/60 p-3 text-left text-sm text-sky-800 hover:bg-sky-50"
                        onClick={() => toggleGeneratedAnimal(groupKey)}
                      >
                        Clique para visualizar {group.exams.length} exame(s) existente(s) deste animal.
                      </button>
                    )}
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )
        ) : requests.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-gray-500">Nenhuma solicitação laboratorial encontrada.</CardContent></Card>
        ) : viewMode === 'kanban' ? (
          <div className="grid gap-4 xl:grid-cols-5 md:grid-cols-2">
            {kanbanColumns.map((column) => {
              const columnRequests = requests.filter((request) => request.status === column.value);
              return (
                <Card
                  key={column.value}
                  className={`min-h-[320px] border-t-4 ${draggingRequestId ? 'border-t-emerald-500' : 'border-t-gray-200'}`}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => handleDropOnStatus(column.value)}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between text-sm">
                      <span>{column.label}</span>
                      <Badge className={getStatusColor(column.value)}>{columnRequests.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {columnRequests.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-400">
                        Nenhuma solicitação nesta etapa.
                      </div>
                    ) : (
                      columnRequests.map((request) => (
                        <div
                          key={request.id}
                          draggable={canUpdateRequests}
                          onDragStart={() => setDraggingRequestId(request.id)}
                          onDragEnd={() => setDraggingRequestId(null)}
                          className={`rounded-lg border bg-white p-3 shadow-sm ${canUpdateRequests ? 'cursor-grab active:cursor-grabbing' : ''}`}
                        >
                          <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-sm text-gray-900">{request.ibama_id}</p>
                              {request.pmp_id && <Badge className="bg-sky-100 text-sky-700">PMP {request.pmp_id}</Badge>}
                            </div>
                            <p className="text-sm text-gray-700">{request.common_name}</p>
                            <p className="text-xs text-gray-500">{request.species || 'Espécie não informada'}</p>
                            <p className="text-xs text-gray-600"><strong>Número:</strong> {request.request_number || 'N/A'}</p>
                            <p className="text-xs text-gray-600"><strong>Exame:</strong> {request.exam_type || (request.requested_exam_types || []).join(', ') || 'N/A'}</p>
                            <p className="text-xs text-gray-600"><strong>Origem:</strong> {request.source_type === 'necropsy' || request.necropsy_id ? 'Necropsia' : 'Acompanhamento'}</p>
                            <NecropsySampleMetadata request={request} />
                            <p className="text-xs text-gray-600"><strong>Laboratório:</strong> {request.laboratory_type === 'externo' ? 'Externo' : 'Interno'}</p>
                            <p className="text-xs text-gray-600"><strong>Base:</strong> {request.base_name || 'N/A'}</p>
                            {request.result_text && <p className="text-xs text-gray-600"><strong>Resultado:</strong> {request.result_text}</p>}
                            {request.notes && <p className="text-xs text-gray-600">{request.notes}</p>}
                            {renderLinkedExams(request)}
                            <div className="flex flex-wrap justify-end gap-2">
                              {canManageRequests && (
                                <>
                                  <Button size="sm" variant="outline" onClick={() => openEditDialog(request)}>
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteRequest(request)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              {canCreateExam && request.status !== 'cancelado' && (
                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openRegisterExamDialog(request)}>
                                  <ClipboardCheck className="w-4 h-4 mr-2" />
                                  Registrar exame
                                </Button>
                              )}
                              {canUpdateRequests && hasPermission(user, 'exams.update') && request.status !== 'cancelado' && (
                                <Button size="sm" variant="outline" onClick={() => openLinkExamDialog(request)}>Vincular exame existente</Button>
                              )}
                              {request.status === 'cancelado' && <Badge className="bg-red-100 text-red-800">Solicitação cancelada</Badge>}
                              <Button size="sm" variant="outline" onClick={() => navigate(`/animals/${request.animal_id}/record`)}>
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Prontuário
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <Card key={request.id} className="border-l-4 border-l-emerald-500">
                <CardHeader className="pb-2">
                  <CardTitle className="flex flex-wrap items-center justify-between gap-3 text-base">
                    <div className="flex items-center gap-2">
                      <FlaskConical className="w-4 h-4 text-emerald-700" />
                      <span>{request.ibama_id} - {request.common_name}</span>
                      {request.pmp_id && <Badge className="bg-sky-100 text-sky-700">PMP {request.pmp_id}</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={getStatusColor(request.status)}>{String(request.status || '').replaceAll('_', ' ')}</Badge>
                      {canManageRequests && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => openEditDialog(request)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Editar
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteRequest(request)}>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Excluir
                          </Button>
                        </>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid gap-2 text-sm md:grid-cols-2">
                    <p><strong>Espécie:</strong> {request.species || 'N/A'}</p>
                    <p><strong>Base:</strong> {request.base_name || 'N/A'}</p>
                    <p><strong>Número:</strong> {request.request_number || 'N/A'}</p>
                    <p><strong>Exame:</strong> {request.exam_type || (request.requested_exam_types || []).join(', ') || 'N/A'}</p>
                    <p><strong>Origem:</strong> {request.source_type === 'necropsy' || request.necropsy_id ? 'Necropsia' : 'Acompanhamento'}</p>
                    <p><strong>Laboratório:</strong> {request.laboratory_type === 'externo' ? 'Externo' : 'Interno'}</p>
                    <p><strong>Solicitante:</strong> {request.requester_name || request.created_by_name || 'N/A'}</p>
                    <p><strong>Data:</strong> {request.requested_at ? new Date(request.requested_at).toLocaleString('pt-BR') : request.created_at ? new Date(request.created_at).toLocaleString('pt-BR') : 'N/A'}</p>
                  </div>
                  {request.notes && <p className="text-sm"><strong>Observação inicial:</strong> {request.notes}</p>}
                  <NecropsySampleMetadata request={request} />
                  {request.result_text && <p className="rounded border bg-emerald-50 p-3 text-sm"><strong>Resultado:</strong> {request.result_text}</p>}
                  {renderLinkedExams(request)}

                  <div className="grid gap-3 md:grid-cols-[180px_1fr_auto]">
                    <div>
                      <Label>Novo status</Label>
                      <Select defaultValue={request.status} onValueChange={(value) => handleUpdateStatus(request.id, value)} disabled={!canUpdateRequests}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Nota / resposta do laboratório</Label>
                      <Textarea
                        value={notesById[request.id] || ''}
                        onChange={(e) => setNotesById((prev) => ({ ...prev, [request.id]: e.target.value }))}
                        rows={2}
                        placeholder="Ex: material recebido, coleta agendada, resultado liberado"
                        disabled={!canUpdateRequests}
                      />
                    </div>
                    <div className="flex items-end">
                      <div className="flex flex-col gap-2 sm:flex-row">
                        {canCreateExam && request.status !== 'cancelado' && (
                          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openRegisterExamDialog(request)}>
                            <ClipboardCheck className="w-4 h-4 mr-2" />
                            Registrar exame
                          </Button>
                        )}
                        {canUpdateRequests && hasPermission(user, 'exams.update') && request.status !== 'cancelado' && (
                          <Button variant="outline" onClick={() => openLinkExamDialog(request)}>Vincular exame existente</Button>
                        )}
                        {request.status === 'cancelado' && <Badge className="bg-red-100 text-red-800">Solicitação cancelada</Badge>}
                        <Button variant="outline" onClick={() => navigate(`/animals/${request.animal_id}/record`)}>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Ver prontuário
                        </Button>
                      </div>
                    </div>
                  </div>

                  {request.updates?.length > 0 && (
                    <div className="rounded border bg-gray-50 p-3 text-sm">
                      <p className="font-medium text-gray-700 mb-2">Histórico</p>
                      <div className="space-y-2">
                        {request.updates.slice().reverse().map((update, index) => (
                          <div key={`${request.id}-update-${index}`} className="rounded bg-white p-2 border">
                            <p><strong>{update.status}</strong> • {update.updated_by_name || 'Sistema'} • {update.updated_at ? new Date(update.updated_at).toLocaleString('pt-BR') : 'N/A'}</p>
                            {update.note && <p>{update.note}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {savingId === request.id && <p className="text-xs text-gray-500">Salvando atualização...</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={Boolean(linkingRequest)} onOpenChange={(open) => {
        if (!open) {
          setLinkingRequest(null);
          setLinkableExams([]);
        }
      }}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vincular exame existente</DialogTitle>
            <DialogDescription>
              Exames do mesmo animal e tipo, ainda sem solicitação vinculada. O status será atualizado conforme o resultado registrado no exame.
            </DialogDescription>
          </DialogHeader>
          {loadingLinkableExams ? (
            <p className="text-sm text-gray-600">Buscando exames...</p>
          ) : linkableExams.length ? (
            <div className="space-y-2">
              {linkableExams.map((exam) => (
                <div key={exam.id} className="flex flex-wrap items-center justify-between gap-3 rounded border p-3">
                  <div className="text-sm">
                    <p className="font-medium">{exam.tipo_exame}</p>
                    <p className="text-gray-600">Coleta: {exam.data_coleta ? new Date(exam.data_coleta).toLocaleString('pt-BR') : 'Não informada'} · {exam.data_resultado || (exam.parametros || []).some((param) => param.valor || param.resultado || param.intensidade || param.agente_identificado) ? 'Com resultado' : 'Sem resultado'}</p>
                  </div>
                  <Button size="sm" onClick={() => handleLinkExam(exam.id)} disabled={Boolean(linkingExamId)}>
                    {linkingExamId === exam.id ? 'Vinculando...' : 'Vincular'}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600">Nenhum exame disponível para esta solicitação.</p>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(registeringRequest)} onOpenChange={(open) => {
        if (!open) {
          setRegisteringRequest(null);
          setRegisteringExamType('');
        }
      }}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registrar exame da solicitação</DialogTitle>
            <DialogDescription>
              O exame será salvo no cadastro real de exames e ficará disponível no prontuário e em Exames gerados.
            </DialogDescription>
          </DialogHeader>
          {registeringRequest && (
            <ExamForm
              key={`${registeringRequest.id}-${registeringExamType}`}
              animalId={registeringRequest.animal_id}
              user={user}
              initialValues={buildExamInitialValuesFromRequest()}
              submitLabel="Registrar exame"
              onCancel={() => {
                setRegisteringRequest(null);
                setRegisteringExamType('');
              }}
              onSaved={handleExamRegistered}
              contextHeader={
                <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold text-emerald-900">Solicitação Nº {registeringRequest.request_number || registeringRequest.id}</p>
                    <Badge className={getStatusColor(registeringRequest.status)}>{String(registeringRequest.status || '').replaceAll('_', ' ')}</Badge>
                  </div>
                  <div className="grid gap-2 text-sm md:grid-cols-2">
                    <p><strong>Animal:</strong> {registeringRequest.ibama_id || registeringRequest.animal_id}</p>
                    <p><strong>Nome comum:</strong> {registeringRequest.common_name || 'N/A'}</p>
                    <p><strong>Espécie:</strong> {registeringRequest.species || 'N/A'}</p>
                    <p><strong>Solicitado por:</strong> {registeringRequest.requester_name || registeringRequest.created_by_name || 'N/A'}</p>
                    <p><strong>Laboratório:</strong> {registeringRequest.laboratory_type === 'externo' ? 'Externo' : 'Interno'}</p>
                    <p><strong>Base:</strong> {registeringRequest.base_name || 'N/A'}</p>
                  </div>
                  <div>
                    <Label>Qual exame deseja registrar?</Label>
                    <Select value={registeringExamType} onValueChange={setRegisteringExamType}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {getRequestedExamTypes(registeringRequest).map((type) => {
                          const linked = (registeringRequest.linked_exams || []).find((item) => item.exam_type === type);
                          return (
                            <SelectItem key={type} value={type}>
                              {type} - {linked?.status ? linked.status.replace('_', ' ') : 'pendente'}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-sm"><strong>Exame solicitado:</strong> {getRequestedExamTypes(registeringRequest).join(', ') || 'N/A'}</p>
                  {registeringRequest.notes && <p className="text-sm"><strong>Observações da solicitação:</strong> {registeringRequest.notes}</p>}
                  {renderLinkedExams(registeringRequest)}
                </div>
              }
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editingRequest)} onOpenChange={(open) => !open && setEditingRequest(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar solicitação de exame</DialogTitle>
            <DialogDescription>
              Ajuste os dados administrativos da solicitação selecionada.
            </DialogDescription>
          </DialogHeader>
          {editingRequest && (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Número</Label>
                  <Input value={editingRequest.request_number || 'Sem número'} disabled />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editForm.status} onValueChange={(value) => updateEditForm('status', value)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo de exame *</Label>
                  <Input value={editForm.exam_type} onChange={(e) => updateEditForm('exam_type', e.target.value)} />
                </div>
                <div>
                  <Label>Laboratório</Label>
                  <Select value={editForm.laboratory_type} onValueChange={(value) => updateEditForm('laboratory_type', value)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="interno">Interno</SelectItem>
                      <SelectItem value="externo">Externo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Data</Label>
                  <Input type="date" value={editForm.requested_date} onChange={(e) => updateEditForm('requested_date', e.target.value)} />
                </div>
                <div>
                  <Label>Hora</Label>
                  <Input type="time" value={editForm.requested_time} onChange={(e) => updateEditForm('requested_time', e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label>Responsável solicitante</Label>
                  <Input value={editForm.requester_name} onChange={(e) => updateEditForm('requester_name', e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Observação da solicitação</Label>
                <Textarea value={editForm.notes} onChange={(e) => updateEditForm('notes', e.target.value)} rows={2} />
              </div>
              <div>
                <Label>Resultado</Label>
                <Textarea value={editForm.result_text} onChange={(e) => updateEditForm('result_text', e.target.value)} rows={3} />
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" onClick={() => setEditingRequest(null)} disabled={savingId === editingRequest.id}>
                  Cancelar
                </Button>
                <Button type="button" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSaveEdit} disabled={savingId === editingRequest.id}>
                  {savingId === editingRequest.id ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
